// src/components/common/Header/Header.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Menu, 
  Search, 
  Bell, 
  User, 
  Settings, 
  LogOut, 
  ChevronDown,
  Building2,
  Shield,
  Monitor,
  Store
} from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAlert } from '../../../contexts/AlertContext';
import { getImageUrl } from '../../../utils/imageUtils';
import { usePermissions } from '../../../hooks/usePermissions';
import { getShops } from '../../../services/api/shops';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import HeaderSearch from './HeaderSearch';
import MobileHeaderSearch from './MobileHeaderSearch';
import styles from './Header.module.css';

const Header = ({ onToggleSidebar, sidebarCollapsed }) => {
  const { user, logout } = useAuth();
  const { t } = useLocalization();
  const { theme } = useTheme();
  const { confirm } = useAlert();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const userMenuRef = useRef(null);
  const notificationRef = useRef(null);
  const posMenuRef = useRef(null);
  const { hasPermission, userPermissions } = usePermissions();
  const [terminalShops, setTerminalShops] = useState([]);
  const [showPosMenu, setShowPosMenu] = useState(false);

  // POS shops this user is assigned to (same list as the sidebar's "Open Terminal")
  useEffect(() => {
    if (!hasPermission('create_offline_sales_order')) {
      setTerminalShops([]);
      return;
    }
    getShops({ type: 'pos', assigned: true, limit: 50 })
      .then(data => setTerminalShops(data.shops || (Array.isArray(data) ? data : [])))
      .catch(() => setTerminalShops([]));
  }, [userPermissions]); // eslint-disable-line react-hooks/exhaustive-deps

  const openTerminal = (shopId) => {
    setShowPosMenu(false);
    navigate(`/offline-sales/terminal/${shopId}`);
  };

  // One shop → open it straight away; several → show the picker
  const handlePosClick = () => {
    if (terminalShops.length === 1) openTerminal(terminalShops[0].id);
    else setShowPosMenu(open => !open);
  };

  // Determine if theme is dark
  const isDarkTheme = theme?.includes('dark');

  // Determine logo based on business name and theme
  const getLogoSrc = (size = 'desktop') => {
    const variant = isDarkTheme ? 'main-white' : 'main-dark';
    
    // Size mappings
    const logoMap = {
      'desktop': {
        '1x': `/assets/logo/rettzy-logo-header-desktop-180x45-${variant}.svg`,
        '2x': `/assets/logo/rettzy-logo-header-desktop-360x90-${variant}.svg`
      },
      'tablet': {
        '1x': `/assets/logo/rettzy-logo-header-tablet-160x40-${variant}.svg`,
        '2x': `/assets/logo/rettzy-logo-header-tablet-320x80-${variant}.svg`
      },
      'mobile': {
        '1x': `/assets/logo/rettzy-logo-header-mobile-120x30-${variant}.svg`,
        '2x': `/assets/logo/rettzy-logo-header-mobile-240x60-${variant}.svg`
      }
    };

    return logoMap[size];
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (posMenuRef.current && !posMenuRef.current.contains(event.target)) {
        setShowPosMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    const shouldLogout = await confirm(
      t('header.areYouSureLogout'),
      {
        confirmText: t('nav.logout'),
        cancelText: t('common.cancel'),
        type: 'warning'
      }
    );

    if (shouldLogout) {
      logout();
    }
  };

  const notifications = [];

  const unreadCount = notifications.filter(n => n.unread).length;

  // Check if business name exists
  const hasBusinessName = user?.business?.name && user.business.name.trim() !== '';

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button 
            className={styles.sidebarToggle}
            onClick={onToggleSidebar}
            title={sidebarCollapsed ? t('sidebar.expandTooltip') : t('sidebar.collapseTooltip')}
          >
            <Menu size={20} />
          </button>
          
          <div className={styles.logo}>
            <Link to="/dashboard" className={styles.logoLink}>
              {hasBusinessName ? (
                <>
                  <Building2 size={24} className={styles.logoIcon} />
                  <span className={styles.logoText}>{user.business.name}</span>
                </>
              ) : (
                <picture>
                  {/* Desktop logo */}
                  <source 
                    media="(min-width: 1025px)" 
                    srcSet={`${getLogoSrc('desktop')['1x']} 1x, ${getLogoSrc('desktop')['2x']} 2x`}
                  />
                  {/* Tablet logo */}
                  <source 
                    media="(min-width: 769px) and (max-width: 1024px)" 
                    srcSet={`${getLogoSrc('tablet')['1x']} 1x, ${getLogoSrc('tablet')['2x']} 2x`}
                  />
                  {/* Mobile logo */}
                  <source 
                    media="(max-width: 768px)" 
                    srcSet={`${getLogoSrc('mobile')['1x']} 1x, ${getLogoSrc('mobile')['2x']} 2x`}
                  />
                  <img 
                    src={getLogoSrc('desktop')['1x']} 
                    alt="Rettzy" 
                    className={styles.logoImage}
                  />
                </picture>
              )}
            </Link>
          </div>
        </div>

        <div className={styles.headerCenter}>
          <HeaderSearch />
        </div>

        <div className={styles.headerRight}>
          {/* Mobile Search Button */}
          <button 
            className={`${styles.mobileSearchButton} ${styles.iconButton}`}
            onClick={() => setShowMobileSearch(true)}
            title={t('header.search')}
          >
            <Search size={20} />
          </button>

          {/* POS Terminal quick access — only when the user can open at least one terminal */}
          {terminalShops.length > 0 && (
            <div className={styles.posWrapper} ref={posMenuRef}>
              <button
                className={styles.posButton}
                onClick={handlePosClick}
                title={t('sidebar.openTerminal')}
              >
                <Monitor size={18} />
                <span className={styles.posLabel}>POS</span>
                {terminalShops.length > 1 && <ChevronDown size={14} />}
              </button>

              {showPosMenu && (
                <div className={styles.posDropdown}>
                  <div className={styles.posDropdownTitle}>{t('sidebar.openTerminal')}</div>
                  {terminalShops.map(shop => (
                    <button
                      key={shop.id}
                      className={styles.posDropdownItem}
                      onClick={() => openTerminal(shop.id)}
                    >
                      <Store size={16} />
                      <span>{shop.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Language Selector */}
          <LanguageSelector variant="header" />

          {/* Notifications */}
          <div className={styles.notificationWrapper} ref={notificationRef}>
            <button 
              className={`${styles.notificationButton} ${styles.iconButton}`}
              onClick={() => setShowNotifications(!showNotifications)}
              title={t('header.notifications')}
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className={styles.notificationBadge}>{unreadCount}</span>
              )}
            </button>

            {showNotifications && (
              <div className={styles.notificationDropdown}>
                <div className={styles.notificationHeader}>
                  <h3>{t('header.notifications')}</h3>
                  {unreadCount > 0 && (
                    <button className={styles.markAllRead}>{t('header.markAllRead')}</button>
                  )}
                </div>
                <div className={styles.notificationList}>
                  {notifications.length > 0 ? (
                    notifications.map(notification => (
                      <div 
                        key={notification.id} 
                        className={`${styles.notificationItem} ${notification.unread ? styles.unread : ''}`}
                      >
                        <div className={styles.notificationContent}>
                          <p>{notification.message}</p>
                          <span className={styles.notificationTime}>{notification.time}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className={styles.noNotifications}>
                      <p>{t('header.noNotifications')}</p>
                    </div>
                  )}
                </div>
                <div className={styles.notificationFooter}>
                  <Link to="/notifications" className={styles.viewAll}>{t('header.viewAllNotifications')}</Link>
                </div>
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className={styles.userMenuWrapper} ref={userMenuRef}>
            <button
              className={styles.userMenuButton}
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className={styles.userAvatar}>
                {user?.profilePicture ? (
                  <img
                    src={getImageUrl(user.profilePicture)}
                    alt={user.name}
                    className={styles.userAvatarImg}
                  />
                ) : (
                  <User size={18} />
                )}
              </div>
              <div className={styles.userInfo}>
                <span className={styles.userName}>{user?.name}</span>
                <span className={styles.userRole}>{user?.role?.name}</span>
              </div>
              <ChevronDown size={16} className={`${styles.chevron} ${showUserMenu ? styles.rotated : ''}`} />
            </button>

            {showUserMenu && (
              <div className={styles.userDropdown}>
                <div className={styles.userDropdownHeader}>
                  <div className={styles.userAvatarLarge}>
                    {user?.profilePicture ? (
                      <img
                        src={getImageUrl(user.profilePicture)}
                        alt={user.name}
                        className={styles.userAvatarLargeImg}
                      />
                    ) : (
                      <User size={24} />
                    )}
                  </div>
                  <div className={styles.userDetails}>
                    <div className={styles.userNameLarge}>{user?.name}</div>
                    <div className={styles.userEmail}>{user?.email}</div>
                    <div className={styles.userBusiness}>{user?.business?.name}</div>
                    <div className={styles.userRoleBadge}>{user?.role?.name}</div>
                  </div>
                </div>
                
                <div className={styles.userDropdownMenu}>
                  <Link to="/profile" className={styles.dropdownItem} onClick={() => setShowUserMenu(false)}>
                    <User size={16} />
                    {t('header.profileSettings')}
                  </Link>
                  <Link to="/subscription" className={styles.dropdownItem} onClick={() => setShowUserMenu(false)}>
                    <Settings size={16} />
                    {t('header.businessSettings')}
                  </Link>
                  <Link to="/users/roles" className={styles.dropdownItem} onClick={() => setShowUserMenu(false)}>
                    <Shield size={16} />
                    {t('header.roleManagement')}
                  </Link>
                  <button onClick={handleLogout} className={`${styles.dropdownItem} ${styles.logout}`}>
                    <LogOut size={16} />
                    {t('nav.logout')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Search Overlay */}
      <MobileHeaderSearch 
        show={showMobileSearch}
        onClose={() => setShowMobileSearch(false)}
      />
    </>
  );
};

export default Header;